<script>
  import IconFriends from '@/assets/images/svg/icons/persons.svg';
  import AddFriendsInput from '@/views/challenge-friends/components/AddFriendsInput.vue';

  export default {
    name: 'FriendsList',

    components: {
      AddFriendsInput,
      IconFriends,
    },

    data: () => ({
      search: '',

      headers: [
        {
          text: 'Status',
          align: 'center',
          sortable: false,
          value: 'status',
          width: '5rem',
        },
        { text: 'Username', value: 'username', align: 'left', sortable: false },
        { text: 'Actions', value: 'actions', align: 'right' },
      ],
    }),

    computed: {
      filteredFriends() {
        if (!this.search) {
          return this.items;
        }

        const search = this.search?.toLowerCase();

        return this.items?.filter((friend) => friend.displayName.toLowerCase().includes(search));
      },
      items() {
        return (this.$store.state.allUserFriends || []).sort((a, b) => b.online - a.online);
      },
    },

    methods: {
      removeFriend(item) {
        this.$store.dispatch('showConfirmDialog', {
          title: 'Removing friend',
          text: `Are you sure you want to remove ${item.displayName} from friends?`,
          callback: () => {
            this.removeFriendConfirm(item);
          },
        });
      },
      blockUser(item) {
        this.$store.dispatch('showConfirmDialog', {
          title: 'Blocking user',
          text: `Are you sure you want to block ${item.displayName}?`,
          callback: () => {
            this.blockItemConfirm(item);
          },
        });
      },
      async blockItemConfirm(user) {
        const response = await this.$store.dispatch('blockuser', user.friendId);
        const text = response.status === 'SUCCESS' ? 'User blocked successfully' : "Couldn't block the user";

        await this.$store.dispatch('showNotificationTooltip', text);
        await this.$store.dispatch('hideConfirmDialog');
      },
      async removeFriendConfirm(user) {
        const response = await this.$store.dispatch('removefriend', user.friendId);
        const text = response.status === 'SUCCESS' ? 'Friend removed' : "Couldn't remove friend";

        await this.$store.dispatch('showNotificationTooltip', text);
        await this.$store.dispatch('hideConfirmDialog');
      },
    },
  };
</script>

<template>
  <div>
    <div
      class="friends-container font-size-24 white--text font-weight-600 d-flex align-center flex-wrap justify-space-between mb-4 gap-2"
    >
      <div class="friends-wrapper">
        <div>
          <IconFriends class="mr-2" />
          <span class="friends-title">Friends List</span>
        </div>
        <AddFriendsInput v-if="$vuetify.breakpoint.width < 580" class="add-friends-btn" />
      </div>

      <div class="d-flex gap-2 flex-wrap-reverse inputs-wrapper align-center">
        <v-text-field
          v-model="search"
          class="search-input"
          clearable
          color="white"
          dense
          hide-details
          label="Search friends list..."
          outlined
          prepend-inner-icon="mdi-magnify"
          solo
        />
        <AddFriendsInput v-if="$vuetify.breakpoint.width >= 580" class="add-friends-btn" />
      </div>
    </div>

    <div class="data-table">
      <v-data-table
        :class="{ 'no-headers': !filteredFriends.length }"
        :headers="headers"
        :items="filteredFriends"
        disable-sort
        hide-default-footer
        mobile-breakpoint=""
        no-data-text="Invite your first friend"
      >
        <template #item.status="{ item }">
          <div class="d-flex align-center">
            <span :class="{ online: item.online }" class="dot"></span>
            <span>{{ item.online ? 'online' : 'offline' }}</span>
          </div>
        </template>
        <template #item.username="{ item }">
          <div class="d-flex align-center">
            <img alt="user" class="user-avatar" src="/images/figures/user.webp?v=1" />
            <span class="ml-2 font-weight-600">{{ item.displayName }}</span>
          </div>
        </template>
        <template #item.actions="{ item }">
          <v-menu class="custom-menu" content-class="custom-menu" nudge-left="90px">
            <template v-slot:activator="{ on, attrs }">
              <v-btn color="white" icon text v-bind="attrs" v-on="on">
                <v-icon>mdi-dots-horizontal</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item class="white--text" @click="removeFriend(item)">
                <v-list-item-title>Remove Friend</v-list-item-title>
              </v-list-item>
              <v-list-item class="error-red--text" @click="() => {}">
                <v-list-item-title @click="blockUser(item)">Block User</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  @import 'src/scss/mixins';

  @include dark-table();

  .data-table {
    -webkit-overflow-scrolling: touch;
    position: relative;
  }

  .gap-2 {
    gap: 16px;
  }

  .search-input {
    &::v-deep {
      .v-input__slot {
        background: rgba(255, 255, 255, 0.1) !important;

        input,
        label,
        .v-icon {
          color: #aba6b4 !important;
        }

        input,
        label {
          font-size: 14px !important;
        }

        .v-text-field__slot input {
          padding: unset !important;
        }

        .v-input__prepend-inner {
          margin-top: 2px !important;
        }
      }
    }
  }

  .dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: red;
    margin-right: 4px;
    margin-top: 3px;

    &.online {
      background: #22ce33;
    }
  }

  .user-avatar {
    width: 36px;
    height: 36px;
  }

  .friends-container {
    container-name: friends-container;
    container-type: inline-size;

    @container friends-container (width >= 0px) {
      .friends-wrapper {
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: stretch;
        gap: 16px;
      }

      .friends-title {
        color: #fff;
        font-size: 24px;
        font-style: normal;
        font-weight: 600;
        line-height: 150%;
      }

      .add-friends-btn {
        width: 100% !important;
      }
    }

    @container friends-container (width >= 420px) {
      &::v-deep .add-friends-btn.button-like .v-input__prepend-inner {
        flex-basis: 65% !important;
      }
    }

    @container friends-container (width >= 520px) {
      .add-friends-btn {
        width: auto !important;
      }

      &::v-deep .add-friends-btn.button-like .v-input__prepend-inner {
        flex-basis: 60% !important;
      }
    }

    @container friends-container (width >= 745px) {
      .friends-wrapper {
        width: auto;
      }
    }

    @container friends-container (width < 745px) {
      .inputs-wrapper {
        width: 100%;
        flex-grow: 2;
      }
    }

    @container friends-container (width < 500px) {
      .inputs-wrapper {
        flex-direction: column-reverse !important;

        .search-input {
          width: 100%;
        }
      }
    }
  }
</style>

<style lang="scss">
  .custom-menu {
    background: transparent !important;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.4);
    border-radius: 10px;

    .v-list.v-sheet {
      transition: all 0.3s;
      background: transparent !important;

      .v-list-item {
        min-height: 32px !important;

        .v-list-item__title {
          font-weight: 500;
          font-size: 14px;
        }
      }
    }
  }
</style>
